import React, { CSSProperties, ReactNode } from 'react'
import Icon from '../Icon'
import css from './../maxLines.module.css'

type Props = {
    backgroundColor?: string
    before?: React.ReactNode
    buttonId?: string
    canShowMore?: boolean
    className?: string
    fallBackLineHeight?: number
    forceUpdate?: boolean
    lessButton?: boolean
    lineHeight?: number
    maxLines?: number
    onClickMore?: () => void
    preventShowMore?: boolean
    quoted?: boolean
    readLessText?: string
    readMoreText?: string
    tagName?: string
    text: ReactNode
}

type State = {
    showMore: boolean
}

class MaxLines extends React.Component<Props, State> {
    private lineHeight: number
    private el: HTMLElement | undefined
    constructor(props: Props) {
        super(props)
        this.hasOverflow = this.hasOverflow.bind(this)
        this.onShowMore = this.onShowMore.bind(this)
        this.calcLineHeight = this.calcLineHeight.bind(this)
        this.onShowLess = this.onShowLess.bind(this)
        this.lineHeight = props.lineHeight || 21
        this.state = {
            showMore: false,
        }
    }

    componentDidMount() {
        this.props.forceUpdate && this.forceUpdate()
    }

    hasOverflow() {
        if (!this.el) {
            return true
        }

        return this.el.scrollHeight > this.el.clientHeight
    }

    calcLineHeight() {
        if (!this.el) {
            return
        }
        this.el.style.overflow = 'scroll'
        const innerHTML = this.el.innerHTML
        this.el.innerHTML = ''
        const scrollHeight = this.el.scrollHeight
        this.el.innerHTML = '<br />'
        const lineHeight = this.el.scrollHeight - scrollHeight
        this.el.innerHTML = innerHTML
        this.el.style.overflow = this.state.showMore ? 'visible' : 'hidden'

        this.lineHeight = lineHeight > 10 ? lineHeight : this.props.fallBackLineHeight || 21
    }

    onShowMore(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) {
        const { onClickMore, preventShowMore } = this.props
        if (preventShowMore) {
            return
        }
        e.preventDefault()
        e.stopPropagation()
        this.setState({ showMore: true })

        onClickMore && onClickMore()
    }

    onShowLess(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault()
        e.stopPropagation()
        this.setState({ showMore: false })
        setTimeout(() => {
            this.forceUpdate()
        }, 50)
    }

    formatLineBreaks(node: ReactNode) {
        if (typeof node === 'string') {
            const lines = node.split('\n')
            return lines.map((item, key) => {
                if (item.length > 0) {
                    return (
                        <span key={key}>
                            {item}
                            <br />
                        </span>
                    )
                } else if (lines[key + 1] && lines[key + 1].length) {
                    return (
                        <span key={key}>
                            <br />
                        </span>
                    )
                }
            })
        }

        return node
    }

    render() {
        const {
            className,
            text,
            maxLines = 2,
            buttonId,
            lessButton,
            canShowMore = true,
            tagName = 'div',
            before,
            backgroundColor = '#fff',
            quoted = false,
            readMoreText = 'Lees meer',
            readLessText = 'Lees minder',
        } = this.props
        const { showMore } = this.state
        const hasOverflow = this.hasOverflow()
        const style: CSSProperties = {
            maxHeight: showMore ? '100%' : this.lineHeight * maxLines,
            lineHeight: this.lineHeight && this.lineHeight + 'px',
            margin: 0,
            overflow: showMore ? 'visible' : 'hidden',
            position: 'relative',
        }
        const backgroundImage = `linear-gradient(90deg,hsla(0,0%,100%,0),${backgroundColor} 25%)`
        const quote = (quoted && <span className={css.quote}>"</span>) || null
        return (
            <>
                {React.createElement(
                    tagName,
                    {
                        ref: (el: HTMLElement) => {
                            this.el = el
                        },
                        style,
                        className: [showMore ? '' : css.less, className ? className : ''].join(' '),
                    },
                    <>
                        {before}
                        {quote}
                        <span>{this.formatLineBreaks(text)}</span>
                        {(!hasOverflow || (hasOverflow && !showMore)) && quote}
                        {hasOverflow &&
                            !showMore &&
                            (canShowMore ? (
                                <span
                                    onClick={this.onShowMore}
                                    id={buttonId}
                                    className={[css.more, css.link].join(' ')}
                                    style={{ backgroundImage }}>
                                    {' '}
                                    {readMoreText}...
                                </span>
                            ) : (
                                <span
                                    className={[css.more, css.ellipsis].join(' ')}
                                    style={{ backgroundImage }}>
                                    {' '}
                                    ...{quote}
                                </span>
                            ))}
                        {showMore && lessButton && (
                            <button
                                id={buttonId}
                                className={css.lessButton}
                                onClick={this.onShowLess}>
                                <div className={css.content}>
                                    {readLessText}{' '}
                                    <Icon name="expand_less" className="colorCta" fontSize={20} />
                                </div>
                            </button>
                        )}
                    </>,
                )}
            </>
        )
    }
}

export default MaxLines
